// Public, no-login page a casualty opens from the texted Phone Finder link. It
// reads the phone's GPS and posts the position back to the backend, keyed only by
// the ?token= in the URL. It is rendered directly by index.js — OUTSIDE the auth
// gate, MSAL and the app providers — so it never triggers a sign-in redirect.
//
// Geolocation only works in a secure context (HTTPS, or localhost for testing).
// We watch the position with high accuracy, show it improving, and post one good
// fix once it is accurate enough (or the best fix we have when time runs out).
// The OS grid reference is computed client-side (shared geo.jsx maths) so a lost
// person can read it out to 999/112 even if the send fails.
import { useEffect, useRef, useState } from "react";
import {
  Box, Typography, CircularProgress, Alert, AlertTitle, Button, Stack, Paper,
} from "@mui/material";
import { API_BASE_URL } from "./../config";
import { latLngToOsGridRef } from "./../geo";

// Accept a fix once it is at least this accurate (metres) and we've watched for
// at least MIN_WAIT (first fixes are often cached/coarse). If we never reach it,
// send the best fix we have when MAX_WAIT elapses.
const DESIRED_ACCURACY_M = 30;
const MIN_WAIT_MS = 8000;
const MAX_WAIT_MS = 60000;
const MAX_SEND_ATTEMPTS = 5;
const SEND_RETRY_MS = 10000;

const tokenFromUrl = () =>
  new URLSearchParams(window.location.search).get("token") || "";

const prettyGrid = (gr) =>
  gr ? gr.replace(/^([A-Z]{2})(\d+)$/, (_, l, d) =>
    `${l} ${d.slice(0, d.length / 2)} ${d.slice(d.length / 2)}`) : gr;

const postJson = async (path, body) => {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};

export default function PhoneFind() {
  const token = tokenFromUrl();
  // checking | invalid | expired | locating | sending | sent | sendfailed | geoerror
  const [phase, setPhase] = useState("checking");
  const [pos, setPos] = useState(null);       // { lat, lng, accuracy, grid }
  const [geoError, setGeoError] = useState(null); // { code, message }
  const started = useRef(false);              // guard against double-invocation

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    if (!token) { setPhase("invalid"); return; }

    let watchId = null;
    let maxWaitTimer = null;
    let startTime = 0;
    let best = null;      // best fix seen so far
    let done = false;     // stop after we accept/error once

    const clearWatchers = () => {
      if (watchId != null) navigator.geolocation.clearWatch(watchId);
      if (maxWaitTimer) clearTimeout(maxWaitTimer);
      watchId = null; maxWaitTimer = null;
    };

    const toFix = (p) => {
      const lat = p.coords.latitude, lng = p.coords.longitude;
      return { lat, lng, accuracy: p.coords.accuracy, grid: latLngToOsGridRef(lat, lng, 10) };
    };

    // Send one fix, retrying a few times before telling the user to call 999.
    const sendPosition = async (fix, attempt = 1) => {
      setPhase("sending");
      try {
        await postJson(`/phone-find/${encodeURIComponent(token)}/position`, {
          latitude: fix.lat, longitude: fix.lng, accuracy: fix.accuracy, grid: fix.grid,
        });
        setPhase("sent");
      } catch (e) {
        if (attempt >= MAX_SEND_ATTEMPTS) { setPhase("sendfailed"); return; }
        setTimeout(() => sendPosition(fix, attempt + 1), SEND_RETRY_MS);
      }
    };

    const accept = (fix) => {
      if (done) return;
      done = true;
      clearWatchers();
      setPos(fix);
      sendPosition(fix);
    };

    const onPosition = (p) => {
      if (done) return;
      const fix = toFix(p);
      best = (!best || fix.accuracy < best.accuracy) ? fix : best;
      setPos(best);
      const elapsed = Date.now() - startTime;
      if (best.accuracy <= DESIRED_ACCURACY_M && elapsed >= MIN_WAIT_MS) accept(best);
    };

    const onGeoError = (err) => {
      if (done) return;
      done = true;
      clearWatchers();
      setGeoError({ code: err.code, message: err.message });
      setPhase("geoerror");
      // Best-effort report to control (don't block the UI on it).
      postJson(`/phone-find/${encodeURIComponent(token)}/error`, {
        error_code: err.code, error_message: err.message,
      }).catch(() => {});
    };

    const beginLocating = () => {
      if (!("geolocation" in navigator)) {
        onGeoError({ code: 0, message: "Device not equipped with GPS." });
        return;
      }
      setPhase("locating");
      startTime = Date.now();
      watchId = navigator.geolocation.watchPosition(onPosition, onGeoError, {
        enableHighAccuracy: true, maximumAge: 0, timeout: MAX_WAIT_MS,
      });
      // When time runs out, send the best fix we managed to get (if any).
      maxWaitTimer = setTimeout(() => {
        if (done) return;
        if (best) accept(best);
        // else: leave the watch running until its own timeout fires onGeoError.
      }, MAX_WAIT_MS);
    };

    // First confirm the link is still valid, then start locating.
    fetch(`${API_BASE_URL}/phone-find/${encodeURIComponent(token)}`)
      .then((r) => r.json())
      .then((s) => {
        if (!s || !s.valid) { setPhase(s && s.expired ? "expired" : "invalid"); return; }
        beginLocating();
      })
      .catch(() => beginLocating()); // if the check itself fails, try to locate anyway

    return clearWatchers;
  }, [token]);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default", p: 2,
               display: "flex", justifyContent: "center", alignItems: "flex-start" }}>
      <Paper sx={{ maxWidth: 520, width: "100%", mt: { xs: 2, sm: 6 }, p: 3 }} elevation={3}>
        <Typography variant="h5" gutterBottom>
          Shelter Stone Medical &amp; Rescue Services
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          Phone Finder
        </Typography>

        {phase === "checking" && (
          <Stack direction="row" spacing={2} sx={{ alignItems: "center", mt: 2 }}>
            <CircularProgress size={22} />
            <Typography>Checking your link…</Typography>
          </Stack>
        )}

        {(phase === "invalid" || phase === "expired") && (
          <Alert severity="error" sx={{ mt: 2 }}>
            <AlertTitle>{phase === "expired" ? "Link expired" : "Invalid link"}</AlertTitle>
            {phase === "expired"
              ? "This locate link is no longer active. Please contact Event Control for a new one."
              : "This link is not valid. Please check the message and try again, or contact Event Control."}
          </Alert>
        )}

        {(phase === "locating" || phase === "sending" || phase === "sent"
          || phase === "sendfailed") && (
          <>
            {phase === "locating" && (
              <Stack direction="row" spacing={2} sx={{ alignItems: "center", mt: 2, mb: 1 }}>
                <CircularProgress size={22} />
                <Typography>
                  Finding your location — please keep this page open and hold your
                  phone up in the open air. This can take up to a minute.
                </Typography>
              </Stack>
            )}
            {phase === "sending" && (
              <Stack direction="row" spacing={2} sx={{ alignItems: "center", mt: 2, mb: 1 }}>
                <CircularProgress size={22} />
                <Typography>Sending your position to Event Control — do not close this page.</Typography>
              </Stack>
            )}
            {phase === "sent" && (
              <Alert severity="success" sx={{ mt: 2, mb: 1 }}>
                <AlertTitle>Position sent</AlertTitle>
                Your position has been sent to Event Control. Stay where you are
                unless you are told otherwise, and keep your phone on.
              </Alert>
            )}
            {phase === "sendfailed" && (
              <Alert severity="warning" sx={{ mt: 2, mb: 1 }}>
                <AlertTitle>Couldn't send your position</AlertTitle>
                Please call Event Control and read out the grid reference below.
              </Alert>
            )}

            {pos && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2"><strong>Grid ref:</strong> {prettyGrid(pos.grid) || "—"}</Typography>
                <Typography variant="body2"><strong>Latitude:</strong> {pos.lat.toFixed(5)}</Typography>
                <Typography variant="body2"><strong>Longitude:</strong> {pos.lng.toFixed(5)}</Typography>
                <Typography variant="body2">
                  <strong>Accuracy:</strong> +/- {Math.round(pos.accuracy)} m
                  {phase === "locating" && " (improving…)"}
                </Typography>
              </Box>
            )}
          </>
        )}

        {phase === "geoerror" && (
          <>
            <Alert severity="error" sx={{ mt: 2 }}>
              <AlertTitle>Couldn't get your location</AlertTitle>
              {geoError?.code === 1 && (
                <>Location permission was refused. Please allow location access for your
                  browser in your phone settings, then reload this page.</>
              )}
              {geoError?.code === 2 && (
                <>Your position is currently unavailable. Move into the open, hold your
                  phone up, and reload this page to try again.</>
              )}
              {geoError?.code === 3 && (
                <>It took too long to get a location. Reload this page to try again,
                  ideally in a more open spot.</>
              )}
              {(geoError?.code === 0 || geoError?.code == null) && (
                <>This device can't provide a GPS location.</>
              )}
            </Alert>
            <Button variant="contained" sx={{ mt: 2 }} onClick={() => window.location.reload()}>
              Try again
            </Button>
          </>
        )}
      </Paper>
    </Box>
  );
}
